/* ==========================================================================
   NOVALISE STUDIO — nv-animations.js
   Drives scroll-reveal for nv-animations.css. Dependency-free, defer-safe.
   - Auto-reveals any [data-nv-reveal] element.
   - Optionally auto-tags common editorial blocks (opt-in via data flag on <html>).
   - Staggers groups via [data-nv-reveal-group] children.
   - Respects prefers-reduced-motion (CSS already shows final state; we just
     avoid observing).
   ========================================================================== */
(function () {
  "use strict";

  var reduce =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function revealNow(el) {
    el.classList.add("nv-in");
  }

  // Auto-tag key editorial blocks so the scroll-reveal applies sitewide
  // without editing every section's Liquid. Conservative + idempotent.
  function tag(el, dir) {
    if (el && !el.hasAttribute("data-nv-reveal")) {
      el.setAttribute("data-nv-reveal", dir || "");
    }
  }
  function autoTag() {
    // Section titles across the store.
    document.querySelectorAll(".shopify-section h2").forEach(function (el) {
      tag(el);
    });
    // Hero copy (above the fold — IntersectionObserver reveals on first paint).
    document.querySelectorAll(".nv-hero-editorial__content").forEach(function (el) {
      tag(el);
    });
    // Editorial break: image slides from left, copy from right.
    document.querySelectorAll(".nv-editorial-break__media").forEach(function (el) {
      tag(el, "left");
    });
    document.querySelectorAll(".nv-editorial-break__content").forEach(function (el) {
      tag(el, "right");
    });
    // Brand-value props: staggered reveal.
    var bv = document.querySelector(".nv-brand-values");
    if (bv && !bv.hasAttribute("data-nv-reveal-group")) {
      bv.setAttribute("data-nv-reveal-group", "");
      bv.setAttribute("data-nv-reveal-step", "110");
      bv.querySelectorAll(":scope > *").forEach(function (el) {
        tag(el);
      });
    }
  }

  function init() {
    // If reduced motion, CSS forces final state; nothing to observe.
    if (reduce) return;

    autoTag();

    // Stagger children inside any [data-nv-reveal-group].
    var groups = document.querySelectorAll("[data-nv-reveal-group]");
    groups.forEach(function (group) {
      var step = parseInt(group.getAttribute("data-nv-reveal-step") || "90", 10);
      var kids = group.querySelectorAll("[data-nv-reveal]");
      kids.forEach(function (kid, i) {
        if (!kid.style.getPropertyValue("--nv-delay")) {
          kid.style.setProperty("--nv-delay", i * step + "ms");
        }
      });
    });

    var targets = document.querySelectorAll("[data-nv-reveal]");
    if (!targets.length) return;

    if (!("IntersectionObserver" in window)) {
      // Old browser: just show everything.
      targets.forEach(revealNow);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            revealNow(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
    );

    targets.forEach(function (el) {
      // Already-visible-on-load elements (above the fold) still animate in
      // for a polished first paint, except if explicitly marked instant.
      if (el.hasAttribute("data-nv-reveal-instant")) {
        revealNow(el);
      } else {
        io.observe(el);
      }
    });

    // Safety net: never leave ABOVE-THE-FOLD content hidden. If an in-view
    // element is still not revealed after 1.6s (IO misfire), force it visible.
    // Below-the-fold elements are left for normal scroll reveal.
    setTimeout(function () {
      var vh = window.innerHeight;
      document.querySelectorAll("[data-nv-reveal]:not(.nv-in)").forEach(function (el) {
        if (el.getBoundingClientRect().top < vh + 120) revealNow(el);
      });
    }, 1600);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  // Re-init on Shopify section render (Theme Editor live preview).
  document.addEventListener("shopify:section:load", init);

  /* ------------------------------------------------------------------------
     ES FIX — collection filter labels render in English ("Availability",
     "Price") because they were custom-named in the Search & Discovery app.
     Those are app config (not theme strings), so translate them client-side.
     Exact text-node match only -> safe. Re-runs when facets re-render.
     Proper fix: rename the filters to Spanish in the Search & Discovery app.
     ------------------------------------------------------------------------ */
  var FACET_ES = {
    "Availability": "Disponibilidad",
    "Price": "Precio",
    "In stock": "En stock",
    "Out of stock": "Agotado",
    "Reset": "Restablecer",
    "Clear all": "Borrar todo",
    "Clear": "Borrar",
    "Apply": "Aplicar",
    "From": "Desde",
    "To": "Hasta",
    "Filter": "Filtrar",
    "Filters": "Filtros",
    "Sort by": "Ordenar por"
  };
  function translateFacets(root) {
    var scope = root || document.body;
    var walk = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    var n, batch = [];
    while ((n = walk.nextNode())) batch.push(n);
    batch.forEach(function (node) {
      var key = (node.nodeValue || "").trim();
      if (FACET_ES.hasOwnProperty(key)) {
        node.nodeValue = node.nodeValue.replace(key, FACET_ES[key]);
      }
    });
  }
  function initFacetTranslate() {
    if (!document.querySelector('[class*="facet"], [class*="filter"], facet-filters-form')) return;
    translateFacets();
    // Facets re-render on filter/sort change; observe and re-translate.
    var host = document.querySelector('[class*="facets"], [class*="filters"], #main-collection-filters, main') || document.body;
    var mo = new MutationObserver(function () { translateFacets(host); });
    mo.observe(host, { childList: true, subtree: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFacetTranslate, { once: true });
  } else {
    initFacetTranslate();
  }
  document.addEventListener("shopify:section:load", initFacetTranslate);

  /* ------------------------------------------------------------------------
     QA FIX — hide empty product-recommendations.
     /recommendations/products can return nothing (new store, no rec data),
     leaving skeleton/empty boxes. After recs have had time to load, if a
     recommendations block still has no real product link, hide its section.
     ------------------------------------------------------------------------ */
  function hideEmptyRecommendations() {
    var recs = document.querySelectorAll(
      "product-recommendations, [class*='recommendation'], [data-section-type*='recommend']"
    );
    recs.forEach(function (rec) {
      var hasProduct = rec.querySelector("a[href*='/products/']");
      if (!hasProduct) {
        var section = rec.closest(".shopify-section") || rec;
        section.style.display = "none";
      }
    });
  }
  function scheduleRecCheck() {
    // recs normally resolve well under 3s; check once after that window.
    setTimeout(hideEmptyRecommendations, 3000);
  }
  if (document.readyState === "complete") {
    scheduleRecCheck();
  } else {
    window.addEventListener("load", scheduleRecCheck, { once: true });
  }
})();
